import { getOutboxRequester, selectCurrentUser } from "@overmap-ai/core"
import { RouterProvider } from "@tanstack/react-router"
import { usePostHog } from "posthog-js/react"
import { memo, useEffect, useMemo, useRef, useState } from "react"

import { DedicatedEnvironmentDialog } from "./components/Dialogs/DedicatedEnvironmentDialog"
import { SessionExpiredDialog } from "./components/Dialogs/SessionExpiredDialog"
import { useOnlineStatus } from "./hooks/utils"
import { router } from "./router"
import { sdk } from "./sdk"
import { useAppSelector } from "./store"
import { selectActiveOrganizationId, selectActiveProjectId, selectIsLoggedIn } from "./store/selectors"

const useConditionalPostHog = () => {
	if (["1", "true"].includes(import.meta.env.REACT_APP_USE_POSTHOG ?? "1")) {
		// REASON: For the lifetime of the app, this hook will always either return a PostHog instance or null.
		//  There is no inconsistent number of hook calls because of this condition.
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return usePostHog()
	}
	return null
}

// TODO: Lazy-load everything needed that needs internet connectivity using @loadable/component
export const App = memo(() => {
	const isLoggedIn = useAppSelector(selectIsLoggedIn)
	const online = useOnlineStatus()
	const posthog = useConditionalPostHog()
	const user = useAppSelector(selectCurrentUser)
	const activeProjectId = useAppSelector(selectActiveProjectId)
	const activeOrganizationId = useAppSelector(selectActiveOrganizationId)

	const [sessionChecked, setSessionChecked] = useState(false)
	const bootstrapStarted = useRef(false)

	useEffect(() => {
		// Bypass double runs when StrictMode is enabled.
		if (bootstrapStarted.current) return
		bootstrapStarted.current = true
		void sdk.main.bootstrapSession().finally(() => setSessionChecked(true))
	}, [])

	useEffect(() => {
		const consents = !!user
		// TODO: user?.profile.consents?.analytics
		if (consents) {
			posthog?.identify(user.id.toString(), {
				email: user.email,
				username: user.username,
			})
		}
	}, [posthog, user])

	useEffect(() => {
		const requester = getOutboxRequester()

		if (isLoggedIn && online && sessionChecked) {
			requester?.start()
		} else {
			requester?.stop()
		}
	}, [isLoggedIn, online, sessionChecked])

	const routerContext = useMemo(() => {
		return {
			isLoggedIn,
			online,
			activeOrganizationId,
			activeProjectId,
		}
	}, [activeOrganizationId, activeProjectId, isLoggedIn, online])

	// `isLoggedIn` is false on a cold boot, so rendering the router before the session check would send a user who
	// *is* signed in to the login page, which would send them straight back, and around again.
	if (!sessionChecked) return null

	return (
		<>
			<SessionExpiredDialog />
			{isLoggedIn && <DedicatedEnvironmentDialog />}
			<RouterProvider router={router} context={routerContext} />
		</>
	)
})

App.displayName = "App"
